<template>
    <div class="category-fights-list">
        <div v-if="initDone" class="category-fights-list__body">
            <div class="is-flex is-justify-content-center is-flex-wrap-wrap gap-3-desktop">
                <QueryInput v-model="searchQuery" :size="'is-small'" />
            </div>

            <div v-for="(fightsForRound, index) in filteredFights" :key="index">
                <h4 class="is-size-6 has-text-centered mt-4 mb-2" v-if="fightsForRound[0].bracketType !== BracketTypeEnum.Kata">
                    <FightRoundName
                        class="mr-1"
                        :round-name="fightsForRound[0].roundName"
                        :bracket-level="fightsForRound[0].bracketLevel"
                    />
                </h4>
                <div v-for="(fight, fightIndex) in fightsForRound" :key="fight.id" class="mb-4">
                    <SingleFight
                        :show-winner-and-loser="true"
                        :event-timezone="null"
                        :fight="fight"
                        :fight-index="fightIndex"
                        :fight-queue-statuses="{}"
                        :tracked-time-for-fights="{}"
                        :start-time="null"
                        :color-schema="colorSchema"
                    >
                        <template v-slot:top-slot>
                            <div class="top-header single-fight-content-row">
                                <span> #{{ fight.fightNumber ?? fightIndexes[fight.id] + 1 }} </span>
                                <span v-if="fight.finished"> 🏁 {{ convertToTimezone(fight.finished, eventTimezone) }} </span>
                            </div>
                        </template>

                        <template v-slot:after-first-competitor="{ fight: slotFight, competitor }">
                            <div v-if="slotFight.winnerCompetitor?.id === competitor.id">
                                <WinnerTag />
                            </div>
                        </template>

                        <template v-slot:after-second-competitor="{ fight: slotFight, competitor }">
                            <div v-if="slotFight.winnerCompetitor?.id === competitor.id">
                                <WinnerTag />
                            </div>
                        </template>
                    </SingleFight>
                </div>
            </div>

            <div v-if="fightsEmpty" class="has-text-centered mt-3">
                {{ translate("category_fights_list.no_fights_to_display") }}
            </div>
        </div>

        <div style="height: 100%" class="is-flex is-align-items-center is-justify-content-center" v-else>
            <LoadingSpinner2 />
        </div>

        <div v-if="initDone && (mats.length > 0 || (fightTime && fightTime > 0))" class="category-fights-list__footer">
            <span v-if="mats.length > 0" class="mr-2">
                <span class="has-text-grey-light mr-1">{{ translate("page.schedule_status.mat") }}:</span>
                <span class="tag is-white">{{ matsAsString }}</span>
            </span>

            <span v-if="fightTime && fightTime > 0">
                <span class="mr-1">{{ translate("page.public_brackets.time_per_fight") }}</span>
                <span class="tag is-white">{{ formatFightTime(fightTime) }}</span>
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from "vue";
    import fightsApi from "../../Api/fights";
    import LoadingSpinner2 from "../LoadingSpinner2.vue";
    import { ComplexFight } from "./types";
    import SingleFight from "./SingleFight.vue";
    import FightRoundName from "../FightRoundName.vue";
    import QueryInput from "../QueryInput.vue";
    import objectMatcher from "../objectMatcher";
    import { convertToTimezone } from "../dateTimeUtils";
    import { translate } from "../translations";
    import { BracketTypeEnum } from "../types";
    import type { ColorSchema, Mat } from "../types";
    import timeFormatter from "../timeFormatter";
    import WinnerTag from "./WinnerTag.vue";
    import { getColorSchema } from "./colorSchema";

    const props = defineProps<{ eventId: number; parameterId: number; eventTimezone: string }>();

    const initDone = ref(false);
    const fights = ref<Array<ComplexFight[]>>([]);
    const fightIndexes = ref<Record<number, number>>({});
    const searchQuery = ref<string>("");

    const fightTime = ref<number | null>(null);
    const mats = ref<Mat[]>([]);
    const colorSchema = ref<ColorSchema>("default");
    const matsAsString = computed(() => {
        return mats.value.map(mat => mat.name).join(", ");
    });

    function fightAsString(fight: ComplexFight) {
        const array = [
            fight.category,
            fight.firstCompetitor.firstName,
            fight.firstCompetitor.lastName,
            fight.firstCompetitor.academy,
            fight.secondCompetitor.firstName,
            fight.secondCompetitor.lastName,
            fight.secondCompetitor.academy,
        ];

        return array.join(" ");
    }

    const filteredFights = computed<Array<ComplexFight[]>>(() => {
        if (!searchQuery.value) {
            return fights.value;
        }

        return fights.value
            .map(fightsForRound => {
                return fightsForRound.filter(fight => {
                    return objectMatcher.matchString(searchQuery.value, fightAsString(fight));
                });
            })
            .filter(fightsForRound => fightsForRound.length > 0);
    });

    const fightsEmpty = computed(() => {
        return filteredFights.value.length === 0 || filteredFights.value.every(fightsForRound => fightsForRound.length === 0);
    });

    function load() {
        initDone.value = false;
        searchQuery.value = "";
        fightTime.value = null;
        fights.value = [];
        fightIndexes.value = {};
        mats.value = [];
        colorSchema.value = "default";

        return fightsApi
            .getPublicBracketFightsOverview(props.eventId, props.parameterId)
            .then(response => {
                fightTime.value = response.fightTime;
                mats.value = response.mats;
                colorSchema.value = getColorSchema(response.scoreboardType);
                //Build indexes
                for (let i = 0; i < response.fights.length; i++) {
                    const fight = response.fights[i];
                    fightIndexes.value[fight.id] = i;
                }
                //Group fights by roundName
                const roundGroups = new Map<string, ComplexFight[]>();
                for (const fight of response.fights) {
                    const group = roundGroups.get(fight.roundName);
                    if (group) {
                        group.push(fight);
                    } else {
                        roundGroups.set(fight.roundName, [fight]);
                    }
                }
                fights.value = Array.from(roundGroups.values());
            })
            .finally(() => {
                initDone.value = true;
            });
    }

    watch(() => props.parameterId, load, { immediate: true });

    function formatFightTime(fightTimeToFormat: number | null) {
        if (!fightTimeToFormat || fightTimeToFormat <= 0) {
            return null;
        }

        return timeFormatter.formatSecondsToMinutesAndSeconds(fightTimeToFormat);
    }
</script>
